import {I18n} from 'aws-amplify/utils';

import {DnsWriteErrorKind} from '../zones/types';

// FNP-1192 — WHAT THE HUMAN IS TOLD WHEN A RECORD WRITE IS REFUSED.
//
// ⚠️ THE CLASSIFIER IS REUSED, THE COPY IS NOT — and the split is deliberate.
//
// `classifyWriteError` (zones/services/writeErrors) turns a GraphQL rejection
// back into a `DnsWriteErrorKind`. That work is about the API's error envelope,
// which is identical for zones and records, so duplicating it here would be
// drift: the same string-prefix parsing in two files, diverging the first time
// the contract adds a class. This module imports it.
//
// The COPY is a different matter. The zones copy talks about zones — "that zone
// no longer exists at the provider", "delete the records first" — and pointing
// a record failure at those sentences would tell an operator to fix the wrong
// thing. Same taxonomy, different nouns and different next steps.
//
// ⚠️ ZONE_NOT_EMPTY IS NOT IN THIS SWITCH. It is a refusal to delete a ZONE
// that still holds records; a record write cannot produce it. It falls to the
// default, which claims nothing.

type WriteErrorCopy = {
    title: string,
    action: string,
}

// Not a module-scope object literal: `I18n.get` must run at call time, after
// the language is configured, or every string is captured in the default locale
// at import.
export const recordWriteErrorCopy = (kind: DnsWriteErrorKind): WriteErrorCopy => {

    switch (kind) {

        // ⚠️ THE NORMAL ANSWER FOR A READ-ONLY CONNECTION, and the one this
        // module works hardest to never reach: the write controls are hidden
        // when `canWriteRecords` is false, so an operator who sees this either
        // had their credential change under them, or found a path around the
        // hiding rule. The copy says what to do rather than implying a bug.
        case 'PERMISSION_DENIED':
            return {
                title: I18n.get('This connection cannot change records'),
                action: I18n.get('The credential is valid but does not carry permission to write DNS records. Reconnect the provider with a credential that can write, in Settings. Nothing has been changed.'),
            };

        case 'AUTHENTICATION_FAILED':
            return {
                title: I18n.get('The connection credential was rejected'),
                action: I18n.get('The token or role for this connection is expired or revoked. Reconnect the provider in Settings — retrying will not help. Nothing has been changed.'),
            };

        // ⚠️ NAMES THE RECORD-SHAPED CAUSES, not the zone-shaped ones. The
        // zone copy blames a malformed zone name, which is never why a record
        // write is refused.
        case 'VALIDATION_REJECTED':
            return {
                title: I18n.get('The provider rejected this record'),
                action: I18n.get('The provider considers this record invalid — most often a value that does not match its type, or a TTL outside the range it allows. Retrying the same values will not help; check the message below and correct them.'),
            };

        case 'NOT_FOUND':
            return {
                title: I18n.get('That record or zone no longer exists at the provider'),
                action: I18n.get('It may already have been deleted, possibly from the provider\'s own console. Reload the list to see the current state.'),
            };

        case 'RATE_LIMITED':
            return {
                title: I18n.get('The provider is rate limiting requests'),
                action: I18n.get('This is temporary. Wait a moment and try again — nothing has been changed.'),
            };

        case 'PROVIDER_UNAVAILABLE':
            return {
                title: I18n.get('The provider could not be reached'),
                action: I18n.get('The provider is down or unreachable. This is temporary and retrying may work — nothing has been changed.'),
            };

        // ⚠️ CLAIMS NOTHING IT HAS NO EVIDENCE FOR. No "try again", because an
        // unclassified failure may well have PARTLY succeeded — and on a batch
        // of record changes that is a real possibility, not a theoretical one.
        // No reassurance that nothing changed, for the same reason.
        default:
            return {
                title: I18n.get('The change did not complete'),
                action: I18n.get('The provider did not return a recognisable reason, and some of the changes in this batch may have been applied. Reload the records to see the current state before trying again.'),
            };
    }
};
